<script lang="ts">
    import { type Permission } from '~/app/enums/Permission';
    import { BadgeStatus } from '~/app/enums/BadgeStatus';
    import type { Benefit } from '~/app/auth/enums/TenantSubscriptionPlan';

    interface Badge {
        label: string;
        type?: BadgeStatus;
    }

    export interface TabItem {
        key?: string;
        label: string;
        value: string | number | boolean;
        link?: string; // default use tabs or url
        disabled?: boolean; // render as non-interactive (no navigation, greyed out)
        // Optional muted count rendered right after the label (e.g. "En cours 5"). Omit → nothing renders.
        // A string lets a caller mark an approximate total, e.g. "300+" when the source is capped.
        count?: number | string;
        badge?: Badge;
        // Small yellow dot after the label: something in that tab needs the user's attention.
        dot?: boolean;
        permission?: Permission;
        // Gate by plan benefit (front) OR by a menu-visibility permission key (back). The
        // latter lets the back distinguish plans the benefit map can't (e.g. Starter vs Essential).
        upsellType?: Benefit;
        upsellPermissionKey?: string;
    }

    export interface TabsConfig {
        items: TabItem[];
        useUrlParams?: boolean; // if true ?tab=value
        paramName?: string; //  (default: 'tab')
    }
</script>

<script setup lang="ts">
    const { canAccessBenefit, isUpsellRestricted, isCrossFamily, trackUpsellRestriction } = useUpsellAccess();

    // A tab is upsell-gated either by a missing plan benefit (front) or by a menu-visibility
    // permission key flagged as plan-restricted (back).
    const isTabUpsell = (item: TabItem): boolean => {
        if (item.upsellPermissionKey) return isUpsellRestricted(item.upsellPermissionKey);
        if (item.upsellType) return !canAccessBenefit(item.upsellType);
        return false;
    };

    // Clicking a routed, upsell-gated tab must open the upsell modal rather than navigate:
    // the global module middleware aborts in-app navigation to a gated route, so without this
    // the click would do nothing. Mirrors the sidebar Submenu pattern.
    const onTabClick = (event: MouseEvent, item: TabItem): void => {
        if (!isTabUpsell(item)) return;
        event.preventDefault();
        useEvent('upsell:modal:open', {
            permissionKey: item.upsellPermissionKey,
            crossFamily: item.upsellPermissionKey ? isCrossFamily(item.upsellPermissionKey) : false,
            type: 'tabs',
            label: t(item.label),
        });
    };

    const props = defineProps({
        modelValue: {
            type: [String, Number, Boolean] as PropType<string | number | boolean>,
            required: false,
            default: undefined,
        },
        content: {
            type: Object as PropType<TabsConfig>,
            required: true,
        },
        fullWidth: {
            type: Boolean,
            default: false,
        },
        color: {
            type: String as PropType<'50' | '100' | '200'>,
            default: '50',
        },
        size: {
            type: String as PropType<'m' | 's'>,
            default: 'm',
        },
    });

    const emit = defineEmits<{
        'update:modelValue': [value: string | number | boolean];
    }>();

    const containerClasses = computed(() => {
        const colorMap = {
            '50': 'bg-gray-50',
            '100': 'bg-gray-100',
            '200': 'bg-gray-200',
        };
        const sizeMap = {
            m: 'h-[38px] gap-1.5 p-1 rounded-xl',
            s: 'h-[28px] gap-1 p-0.5 rounded-lg',
        };
        return [colorMap[props.color], sizeMap[props.size]];
    });

    const tabClasses = computed(() => {
        const sizeMap = {
            m: 'px-3 py-1 text-sm rounded-lg',
            s: 'px-2 py-0.5 text-xs rounded-md',
        };
        return sizeMap[props.size];
    });

    const inactiveTextColor = computed(() => {
        return props.color === '200' ? 'text-gray-500' : 'text-gray-400';
    });

    const route = useRoute();
    const router = useRouter();

    const buttonRefs = ref<Record<number, HTMLElement>>({});
    const container = ref<HTMLElement | null>(null);
    const indicator = ref<HTMLElement | null>(null);

    // Détermine si on utilise les URL params
    const useUrlParams = computed(() => props.content.useUrlParams ?? false);
    const paramName = computed(() => props.content.paramName ?? 'tab');

    // Fonction pour vérifier si un lien est actif
    const isLinkActive = (link: string): boolean => {
        const currentPath = route.path;

        // Vérifie si c'est une correspondance exacte
        if (currentPath === link) return true;

        // Vérifie si le lien est un parent du chemin actuel
        // Par exemple, si link="/dashboard" et currentPath="/dashboard/users"
        if (currentPath.startsWith(link + '/')) return true;

        // Pour gérer les liens avec paramètres
        // Compare aussi avec le fullPath pour les query params
        if (route.fullPath === link) return true;

        return false;
    };

    // Trouve l'index actif
    const activeIndex = computed(() => {
        // D'abord, vérifie les items avec liens
        const linkIndex = props.content.items.findIndex((item) => {
            if (item.link) {
                return isLinkActive(item.link);
            }
            return false;
        });

        // Si un lien est actif, le retourner
        if (linkIndex !== -1) {
            return linkIndex;
        }

        // Sinon, utilise la logique des tabs
        if (useUrlParams.value) {
            const queryValue = route.query[paramName.value];
            if (queryValue) {
                return props.content.items.findIndex((item) => !item.link && item.value === queryValue);
            }
        }

        // Utilise modelValue si disponible
        if (props.modelValue !== undefined) {
            return props.content.items.findIndex((item) => !item.link && item.value === props.modelValue);
        }

        // Par défaut, retourne le premier tab (pas lien, pas désactivé)
        const firstTabIndex = props.content.items.findIndex((item) => !item.link && !item.disabled);
        return firstTabIndex !== -1 ? firstTabIndex : 0;
    });

    const { t } = useTranslation();

    // The currently active tab when it is gated by the plan (else null).
    const activeUpsellTab = computed<TabItem | null>(() => {
        const item = props.content.items[activeIndex.value];
        return item && isTabUpsell(item) ? item : null;
    });

    // Report the upsell exposure each time the user lands on a gated tab (the upsell
    // content of that tab is shown). Keyed on the active tab so switching between tabs
    // re-fires only when the active gated tab actually changes. `label` is the tab name.
    watch(
        activeUpsellTab,
        (tab) => {
            if (!tab) return;
            trackUpsellRestriction('tabs', { benefit: tab.upsellType, permissionKey: tab.upsellPermissionKey, label: t(tab.label) });
        },
        { immediate: true }
    );

    const setActive = async (item: TabItem, index: number) => {
        // Si l'item a un lien, navigation classique
        if (item.link) {
            // Le NuxtLink gère déjà la navigation, on n'a rien à faire ici
            // Mais on peut émettre la valeur si nécessaire
            emit('update:modelValue', item.value);
            return;
        }

        // Pour les tabs (non-liens)
        if (buttonRefs.value[index]) {
            updateIndicatorPosition(buttonRefs.value[index]);
        }

        // Si on utilise les URL params
        if (useUrlParams.value) {
            await router.push({
                query: {
                    ...route.query,
                    [paramName.value]: item.value as string,
                },
            });
        }

        // Émet toujours la valeur
        emit('update:modelValue', item.value);
    };

    const updateIndicatorPosition = (button: HTMLElement) => {
        if (!container.value || !indicator.value || !button) return;

        const containerRect = container.value.getBoundingClientRect();
        const buttonRect = button.getBoundingClientRect();

        indicator.value.style.width = `${buttonRect.width}px`;
        indicator.value.style.height = `${buttonRect.height}px`;
        indicator.value.style.top = `${buttonRect.top - containerRect.top}px`;
        indicator.value.style.left = `${buttonRect.left - containerRect.left}px`;
    };

    // Met à jour l'indicateur quand l'index actif change
    const updateActiveIndicator = () => {
        nextTick(() => {
            const index = activeIndex.value;
            if (index >= 0 && buttonRefs.value[index]) {
                updateIndicatorPosition(buttonRefs.value[index]);
            }
        });
    };

    // Surveille les changements de route pour mettre à jour l'indicateur
    watch(
        () => route.path,
        () => {
            updateActiveIndicator();
        }
    );

    watch(
        () => route.query,
        () => {
            updateActiveIndicator();
        },
        { deep: true }
    );

    // Surveille les changements de modelValue
    watch(
        () => props.modelValue,
        (newValue) => {
            if (useUrlParams.value && !props.content.items.some((item) => item.link)) {
                // Seulement pour les tabs, pas pour les liens
                if (newValue !== route.query[paramName.value]) {
                    router.push({
                        query: {
                            ...route.query,
                            [paramName.value]: newValue as string,
                        },
                    });
                }
            }
            updateActiveIndicator();
        }
    );

    // Surveille le redimensionnement de la fenêtre
    const handleResize = () => {
        updateActiveIndicator();
    };

    onMounted(() => {
        // Initialise avec l'URL param si disponible et si ce n'est pas un lien actif
        if (useUrlParams.value && route.query[paramName.value] && activeIndex.value === -1) {
            emit('update:modelValue', route.query[paramName.value] as string);
        }

        // Délai pour s'assurer que le DOM est prêt
        setTimeout(() => {
            updateActiveIndicator();
        }, 100);

        // Ajoute un listener pour le resize
        window.addEventListener('resize', handleResize);
    });

    onUnmounted(() => {
        window.removeEventListener('resize', handleResize);
    });

    // Force la mise à jour de l'indicateur quand les refs changent
    watch(
        buttonRefs,
        () => {
            updateActiveIndicator();
        },
        { deep: true }
    );
</script>

<template>
    <div
        v-if="content.items.length > 1"
        ref="container"
        class="relative items-center"
        :class="[containerClasses, fullWidth ? 'flex w-full' : 'inline-flex']"
    >
        <div
            ref="indicator"
            class="absolute bg-white shadow-[0_1px_2px_0_rgba(0,0,0,0.10)] transition-all duration-300 ease-out"
            :class="size === 'm' ? 'rounded-lg' : 'rounded-md'"
        ></div>

        <template
            v-for="(item, index) in content.items"
            :key="index"
        >
            <div
                v-if="item.disabled"
                :ref="
                    (el: any) => {
                        if (el) {
                            buttonRefs[index] = el as HTMLElement;
                        }
                    }
                "
                v-can="item.permission ? item.permission : null"
                class="relative z-10 flex items-center gap-2 px-2 py-1 font-inter text-[13px] font-medium leading-normal text-gray-300 cursor-not-allowed select-none"
                :class="{ 'flex-1 justify-center': fullWidth }"
                aria-disabled="true"
            >
                <span>{{ $t(item.label) }}</span>
                <CommonUpsellBadge
                    v-if="isTabUpsell(item)"
                    variant="lightning"
                />
            </div>

            <NuxtLink
                v-else-if="item.link"
                :ref="
                    (el: any) => {
                        if (el?.$el) {
                            buttonRefs[index] = el.$el as HTMLElement;
                        }
                    }
                "
                v-can="item.permission ? item.permission : null"
                :to="item.link"
                class="relative flex gap-2 items-center justify-center font-inter font-normal leading-[1.2] whitespace-nowrap transition-colors z-10"
                :class="[
                    tabClasses,
                    {
                        'text-gray-800': activeIndex === index,
                        [inactiveTextColor + ' hover:text-gray-700']: activeIndex !== index,
                        'flex-1': fullWidth,
                    },
                ]"
                @click="setActive(item, index)"
            >
                <span>
                    {{ $t(item.label) }}
                </span>
                <span
                    v-if="item.count !== undefined"
                    class="font-medium text-branding-mid-grey"
                    >{{ item.count }}</span
                >
                <CommonUpsellBadge
                    v-if="isTabUpsell(item)"
                    variant="lightning"
                />
                <span
                    v-else-if="item.badge?.label"
                    :class="item.badge?.type ? useStatusBackgroundColour(item.badge?.type) : ''"
                    class="px-2 py-1 ml-1 text-xs font-bold text-white rounded-md bg-branding-peppol-blue uppercase min-h-5"
                >
                    {{ $t(item.badge?.label) }}
                </span>
                <span
                    v-if="item.dot"
                    class="size-2 rounded-full border border-branding-brand-yellow bg-branding-brand-yellow-fill"
                    :aria-label="$t('profile_review.dot_label')"
                ></span>
            </NuxtLink>

            <button
                v-else
                :ref="
                    (el: any) => {
                        if (el) {
                            buttonRefs[index] = el as HTMLElement;
                        }
                    }
                "
                type="button"
                class="relative flex gap-2 items-center justify-center font-inter font-normal leading-[1.2] whitespace-nowrap transition-colors z-10"
                :class="[
                    tabClasses,
                    {
                        'text-gray-800': activeIndex === index,
                        [inactiveTextColor + ' hover:text-gray-700']: activeIndex !== index,
                        'flex-1': fullWidth,
                    },
                ]"
                @click="setActive(item, index)"
            >
                {{ $t(item.label) }}
                <span
                    v-if="item.count !== undefined"
                    class="ml-1 font-medium text-branding-mid-grey"
                    >{{ item.count }}</span
                >
                <span
                    v-if="item.badge?.label"
                    class="bg-primary text-white px-3 py-1 rounded-full font-medium text-xs font-inter z-[1]"
                >
                    {{ $t(item.badge.label) }}
                </span>
            </button>
        </template>
    </div>
</template>
